/**
 * TEMP-H-003: Execution Events Timeline Types
 * 
 * TypeScript interfaces for Events v2 timeline API and UI components.
 * Defines contracts for timeline filtering, pagination, and display.
 */

import type { StageName } from '@/lib/shared-types';
import type { EventType } from '@/types/events-v2';

// Timeline Filter Parameters
export interface TimelineFilters {
  stage?: StageName[];
  eventType?: EventType[];
  persona?: string[];
  timeRange?: {
    from: string; // ISO timestamp
    to: string;   // ISO timestamp
  };
}

// Pagination Parameters
export interface TimelinePagination {
  limit?: number;
  cursor?: string;
}

// Timeline API Request
export interface TimelineEventsRequest extends TimelineFilters, TimelinePagination {
  executionId: string;
}

// Artifact Reference for timeline display
export interface ArtifactRef {
  id: string;
  name: string;
  type: 'image' | 'video' | 'audio' | 'text' | 'json' | 'other';
  size?: number;
  storageUri: string;
  publicUrl?: string;
  thumbnail?: string;
  duration?: number; // For video/audio
  dimensions?: { width: number; height: number }; // For images
  // Music-specific properties
  audioType?: 'master' | 'mastered' | 'sidechained' | 'stems' | 'preview';
  waveformUri?: string; // For visual waveform display
  musicMetadata?: {
    preset?: string;
    bpm?: number;
    key?: string;
    genre?: string;
  };
  // Video-specific properties
  videoMetadata?: {
    codec?: string;
    resolution?: string;
    fps?: number;
    bitrate?: number;
  };
}

// Enhanced timeline event with display metadata
export interface TimelineEvent {
  id: string; // Unique event ID for deduplication
  event_type: EventType;
  stage?: StageName;
  execution_id: string;
  timestamp: string;
  payload?: any;
  correlation_id?: string;
  workflow_run_id?: string; // Workflow run ID for traceability
  displayTitle: string; // Human readable title
  displayDescription?: string; // Brief description
  artifactRefs: ArtifactRef[]; // Associated artifacts
  severity: 'info' | 'success' | 'warning' | 'error'; // For badge styling
  isExpandable: boolean; // Whether event has detailed payload
  attemptNumber?: number; // Current attempt for this stage
}

// Timeline API Response
export interface TimelineEventsResponse {
  events: TimelineEvent[];
  pagination: {
    hasNextPage: boolean;
    nextCursor?: string;
    totalCount?: number;
  };
  executionId: string;
}

// Timeline Query Parameters for useExecutionEvents
export interface UseTimelineOptions extends TimelineFilters {
  limit?: number;
  enabled?: boolean;
  refetchInterval?: number | false;
}

// Event Display Categories for filtering UI
export const EVENT_TYPE_CATEGORIES = {
  lifecycle: ['pipeline_started', 'pipeline_completed', 'stage_started', 'stage_completed', 'stage_failed'] as const,
  quality: ['scores_ready', 'qc_completed'] as const,
  content: ['content_ready'] as const,
  billing: ['billing_committed'] as const,
  export: ['export_preview'] as const,
} as const;

export type EventTypeCategory = keyof typeof EVENT_TYPE_CATEGORIES;

// Stage Display Configuration
export interface StageDisplayConfig {
  label: string;
  icon: string;
  color: string;
  order: number;
}

export const STAGE_DISPLAY_CONFIG: Record<StageName, StageDisplayConfig> = {
  script: { label: 'Script', icon: '📝', color: 'blue', order: 1 },
  vision: { label: 'Vision', icon: '👁️', color: 'purple', order: 2 },
  storyboard: { label: 'Storyboard', icon: '🎬', color: 'green', order: 3 },
  shots: { label: 'Shots', icon: '📸', color: 'orange', order: 4 },
  continuity: { label: 'Continuity', icon: '🔗', color: 'teal', order: 5 },
  frames: { label: 'Frames', icon: '🖼️', color: 'pink', order: 6 },
  videos: { label: 'Videos', icon: '🎥', color: 'red', order: 7 },
  elements: { label: 'Elements', icon: '🎨', color: 'indigo', order: 8 },
  music: { label: 'Music', icon: '🎵', color: 'cyan', order: 9 },
  qc: { label: 'Quality Control', icon: '✅', color: 'emerald', order: 10 },
  assemble: { label: 'Assembly', icon: '🔧', color: 'yellow', order: 11 },
  publish: { label: 'Publish', icon: '🚀', color: 'violet', order: 12 },
  music_master: { label: 'Music Master', icon: '🎚️', color: 'slate', order: 13 },
  music_sidechain: { label: 'Music Sidechain', icon: '🎛️', color: 'stone', order: 14 },
  export_preview: { label: 'Export Preview', icon: '🎬', color: 'amber', order: 15 },
};

// Event Type Display Configuration
export interface EventTypeDisplayConfig {
  label: string;
  icon: string;
  severity: TimelineEvent['severity'];
  category: EventTypeCategory;
}

export const EVENT_TYPE_DISPLAY_CONFIG: Record<EventType, EventTypeDisplayConfig> = {
  pipeline_started: { label: 'Pipeline Started', icon: '🏁', severity: 'info', category: 'lifecycle' },
  pipeline_completed: { label: 'Pipeline Completed', icon: '🎯', severity: 'success', category: 'lifecycle' },
  stage_started: { label: 'Stage Started', icon: '▶️', severity: 'info', category: 'lifecycle' },
  stage_completed: { label: 'Stage Completed', icon: '✅', severity: 'success', category: 'lifecycle' },
  stage_failed: { label: 'Stage Failed', icon: '❌', severity: 'error', category: 'lifecycle' },
  content_ready: { label: 'Content Ready', icon: '📦', severity: 'success', category: 'content' },
  scores_ready: { label: 'QC Scores', icon: '📊', severity: 'info', category: 'quality' },
  qc_started: { label: 'QC Started', icon: '🔍', severity: 'info', category: 'quality' },
  qc_completed: { label: 'QC Complete', icon: '✅', severity: 'success', category: 'quality' },
  qc_failed: { label: 'QC Failed', icon: '❌', severity: 'error', category: 'quality' },
  billing_committed: { label: 'Cost Committed', icon: '💰', severity: 'info', category: 'billing' },
  export_preview: { label: 'Export Preview', icon: '📤', severity: 'success', category: 'export' },
  error: { label: 'Error', icon: '🚫', severity: 'error', category: 'lifecycle' },
  processing: { label: 'Processing', icon: '⚙️', severity: 'info', category: 'lifecycle' },
  quality_warning: { label: 'Quality Warning', icon: '⚠️', severity: 'warning', category: 'quality' },
  billing_record_created: { label: 'Billing Record', icon: '💳', severity: 'info', category: 'billing' },
};

// Timeline Cache Keys for React Query
export const timelineKeys = {
  all: ['timeline'] as const,
  execution: (executionId: string) => ['timeline', executionId] as const,
  filtered: (executionId: string, filters: TimelineFilters) => 
    ['timeline', executionId, 'filtered', filters] as const,
} as const;

// WebSocket Timeline Events for live updates
export interface TimelineWebSocketEvents {
  'timeline:event_added': {
    executionId: string;
    event: TimelineEvent;
    timestamp: string;
  };
  'timeline:events_batch': {
    executionId: string;
    events: TimelineEvent[];
    timestamp: string;
  };
  'timeline:event_updated': {
    executionId: string;
    eventId: string;
    updates: Partial<TimelineEvent>;
    timestamp: string;
  };
}

// Event Deduplication Strategy
export type DeduplicationStrategy = 'id' | 'timestamp_hash' | 'correlation_id';

// Timeline Display Preferences
export interface TimelineDisplayPreferences {
  groupByStage: boolean;
  showArtifactThumbnails: boolean;
  compactMode: boolean;
  autoRefresh: boolean;
  defaultFilters: TimelineFilters;
}

// Error states for timeline
export interface TimelineError {
  type: 'fetch_failed' | 'websocket_disconnected' | 'permission_denied' | 'execution_not_found';
  message: string;
  retryable: boolean;
  timestamp: string;
}